import { isMoment } from 'moment';
import { CustomFieldFunctionReturnTypeMode, DataFieldUsage } from 'src/app/services/api.services';
import { escapeHtml, isNullOrUndefined } from 'src/app/shared/utils';
import { InsightsQuery, InsightsQuerySyntax, InsightsQuerySyntaxVisitor } from 'src/insightsQuery';
import { DataType } from '../../dataModel/DataType';
import { CustomFieldIssue } from './CustomFieldIssue';
import { CustomFields } from './CustomFields';
import { VisualCustomField } from '../VisualCustomField';

export namespace CustomFieldSyntaxVisitor {
  export type FunctionResolver = (name: string, args: InsightsQuerySyntax[]) => CustomFields.Function;
  export type FieldResolver = (identifier: InsightsQuery.IdentifierSyntax, context: VisualCustomField | null) => CustomFields.Field;

  export interface Options {
    functionResolver: FunctionResolver;
    fieldResolver: FieldResolver;

    /** Skip circular dependency check. */
    skipCircularDependencyCheck?: boolean;

    field?: string;

    aggregated?: boolean;

    targetDataType?: DataType;

    currentField?: VisualCustomField;
  }

  export interface Result {
    dataType: DataType;
    aggregated?: boolean;
    issues?: CustomFieldIssue[];
  }

  export function visit(syntax: InsightsQuerySyntax, options: Options): Result {
    let result: Result = {
      dataType: DataType.Text,
      aggregated: false,
      issues: [],
    };

    try {
      let value = new ValueValidationVisitor(options, result.issues).visit(syntax, {
        parentAggregates: !!options?.aggregated,
      });

      result.dataType = value.dataType;
      result.aggregated = value.aggregated;

      let anyErrors = !!result.issues.find(x => x.type === CustomFieldIssue.Type.Error);
      if (!anyErrors) {
        if (!options.skipCircularDependencyCheck) {
          try {
            new CirciularDependencyValidationVisitor(options).visit(syntax);
          }
          catch (err) {
            if (err instanceof CirciularDependencyError) {
              let dependencyChain =
                err.fieldStack
                  .map(f => {
                    return (f == options.field.toLowerCase())
                      ? `<strong>${escapeHtml(f)}</strong>`
                      : escapeHtml(f);
                  })
                  .join(' -> ');

              if (!CustomFieldIssue.CIRCULAR_DEPENDENCY.disabled) {
                result.issues.push({
                  type: CustomFieldIssue.Type.Error,
                  location: InsightsQuery.TextLocation.first,
                  message: CustomFieldIssue.CIRCULAR_DEPENDENCY.format(dependencyChain)
                });
              }
            }
            else
              throw err;
          }
        }

        if (!isNullOrUndefined(options.targetDataType)) {
          let conversionType = DataType.classifyConversion(options.targetDataType, result.dataType);
          if (conversionType == DataType.ConversionClassification.Downcast) {
            if (!CustomFieldIssue.FIELD_FORMAT_DOWNCAST.disabled) {
              result.issues.push({
                type: CustomFieldIssue.Type.Warning,
                location: InsightsQuery.TextLocation.first,
                message: CustomFieldIssue.FIELD_FORMAT_DOWNCAST.format(result.dataType.title, options.targetDataType.title)
              });
            }
          }
          else if (conversionType == DataType.ConversionClassification.Invalid) {
            if (!CustomFieldIssue.FIELD_FORMAT_MISMATCH.disabled) {
              result.issues.push({
                type: CustomFieldIssue.Type.Error,
                location: InsightsQuery.TextLocation.first,
                message: CustomFieldIssue.FIELD_FORMAT_MISMATCH.format(result.dataType.title, options.targetDataType.title)
              });
            }
          }
        }
      }
    }
    catch (err) {
      result.issues.push({
        type: CustomFieldIssue.Type.Error,
        location: InsightsQuery.TextLocation.first,
        message: CustomFieldIssue.UNEXPECTED_ERROR.format(err)
      });
    }

    return result;
  }

  interface ValueValidationParams {
    parentAggregates?: boolean;
  }

  interface ValueValidationResult {
    dataType: DataType;
    aggregated?: boolean;
  }

  class ValueValidationVisitor extends InsightsQuerySyntaxVisitor<ValueValidationResult, ValueValidationParams> {
    constructor(
      public readonly options: Options,
      public readonly issues: CustomFieldIssue[]) {
      super();
    }

    readonly fieldResolver = this.options.fieldResolver;
    readonly functionResolver = this.options.functionResolver;
    readonly skipCircularDependencyCheck = this.options.skipCircularDependencyCheck;

    visitLiteral(syntax: InsightsQuery.LiteralSyntax, params: ValueValidationParams) {
      if (Array.isArray(syntax.value) || syntax.value instanceof Set) {
        return { dataType: DataType.List };
      } else if (syntax.value instanceof Map) {
        return { dataType: DataType.Map };
      }

      if (typeof syntax.value === 'boolean') {
        return { dataType: DataType.Boolean };
      }
      else if (typeof syntax.value === 'number') {
        return { dataType: DataType.Number };
      }
      else if (typeof syntax.value === 'bigint') {
        return { dataType: DataType.Integer };
      }
      else if (typeof syntax.value === 'string') {
        return { dataType: DataType.Text };
      }
      else if (syntax.value instanceof Date || isMoment(syntax.value)) {
        return { dataType: DataType.DateTime };
      }
      else if (typeof syntax.value === 'object') {
        return { dataType: DataType.Map };
      }
      else {
        return { dataType: DataType.Text };
      }
    }

    visitUnary(syntax: InsightsQuery.UnarySyntax, params: ValueValidationParams) {
      if (!syntax.right)
        return { dataType: DataType.Text };

      var r = this.visit(syntax.right, params);

      let dataType = r.dataType;

      if (syntax.operator === InsightsQuery.UnaryOperator.Not) {
        this.expectBoolean(syntax.right, r.dataType);

        dataType = DataType.Boolean;
      }
      else {
        this.expectNumeric(syntax.right, r.dataType);
      }

      return {
        ...r,
        dataType
      };
    }

    visitBinary(syntax: InsightsQuery.BinarySyntax, params: ValueValidationParams) {
      let l = this.visit(syntax.left, params);

      if (!syntax.right)
        return l;

      let r = this.visit(syntax.right, params);

      let leftDataType = l.dataType;
      let rightDataType = r.dataType;
     
      if (syntax.operator === InsightsQuery.BinaryOperator.And
        || syntax.operator === InsightsQuery.BinaryOperator.Or) {
        this.expectBoolean(syntax.left, l.dataType);
        this.expectBoolean(syntax.right, r.dataType);
      }
      else if (syntax.operator === InsightsQuery.BinaryOperator.Equal
        || syntax.operator === InsightsQuery.BinaryOperator.NotEqual) {
        this.expectSameType(syntax,
          syntax.left, l.dataType,
          syntax.right, r.dataType);
      }
      else if (
         (syntax.operator === InsightsQuery.BinaryOperator.Concat) ||
        ((syntax.operator === InsightsQuery.BinaryOperator.Add) && (l.dataType.isText || r.dataType.isText))
      ) {
        if (!l.dataType.isText || !r.dataType.isText) {
          // IN-924, Scenario 7
          this.addError(syntax, CustomFieldIssue.IN_924_INVALID_CONCATENATION_OPERANDS);
        }
        else {
          this.expectTextPermissive(syntax.left, l.dataType);
          this.expectTextPermissive(syntax.right, r.dataType);

          if (leftDataType.isText) {
            [leftDataType, rightDataType] = [DataType.Text, rightDataType];
          }
          else {
            [leftDataType, rightDataType] = [DataType.Text, leftDataType];
          }
        }
      }
      else {
        this.expectNumbers(syntax,
          syntax.left, l.dataType,
          syntax.right, r.dataType);
      }

      let dataType = DataType.getLeftSideResult([leftDataType, rightDataType]);
      
      if (syntax.operator === InsightsQuery.BinaryOperator.And
       || syntax.operator === InsightsQuery.BinaryOperator.Or
       || syntax.operator === InsightsQuery.BinaryOperator.Equal
       || syntax.operator === InsightsQuery.BinaryOperator.NotEqual
       || syntax.operator === InsightsQuery.BinaryOperator.LessThan
       || syntax.operator === InsightsQuery.BinaryOperator.LessThanOrEqual
       || syntax.operator === InsightsQuery.BinaryOperator.GreaterThan
       || syntax.operator === InsightsQuery.BinaryOperator.GreaterThanOrEqual) {
        dataType = DataType.Boolean;
      }

      return {
        dataType,
        aggregated: l.aggregated || r.aggregated
      };
    }

    visitFunctionCall(syntax: InsightsQuery.FunctionCallSyntax, params: ValueValidationParams) {
      let func = this.options.functionResolver(syntax.functionName, syntax.args);
      if (!func) {
        this.addError(syntax, CustomFieldIssue.UNKNOWN_FUNCTION, syntax.functionName);
        return { dataType: DataType.Text };
      }

      let argParams = {
        ...params,
        parentAggregates: params.parentAggregates || func.aggregatesFields
      };

      let args = syntax.args.map(x => this.visit(x, argParams));

      let checkArgumentTypes = true;

      if (func.aggregatesFields && syntax.args.length > 0) {
        let fieldSyntax = syntax.args[func.arguments.findIndex(x => x.measureArgument)];
        let invalidInput = true;

        if (fieldSyntax instanceof InsightsQuery.IdentifierSyntax) {
          let f = this.fieldResolver(fieldSyntax, this.options.currentField);
          let target = f ? (typeof f === 'function' ? f() : f) : null;
          if (target) {
            if (target.isAggregated && target.schemaItem?.getDefaultUsage() != DataFieldUsage.Measure) {
              this.addError(fieldSyntax,
                CustomFieldIssue.ALREADY_AGGREGATED,
                target.shortTitle);

              checkArgumentTypes = false;
            }
            else {
              if (DataType.from(func.arguments[0].type).isNumeric && !args[0].dataType.isNumeric) {
                this.addError(syntax.args[0],
                  CustomFieldIssue.AGGREGATION_EXPECTS_NUMERIC_FIELD,
                  func.name);

                checkArgumentTypes = false;
              }
              else {
                invalidInput = false;
              }
            }
          }
        }

        if (invalidInput) {
          this.addError(syntax.args[0],
            CustomFieldIssue.IN_7625_AGGR_FUNC_EXPECTS_FIELD,
            func.name);

          checkArgumentTypes = false;
        }
      }

      if ((func.minArguments === func.maxArguments) && args.length != func.maxArguments) {
        this.addError(syntax,
          CustomFieldIssue.EXPECTED_N_ARGUMENTS,
          func.name,
          func.maxArguments);
      }
      else if (!isNullOrUndefined(func.minArguments) && args.length < func.minArguments) {
        this.addError(syntax,
          CustomFieldIssue.EXPECTED_AT_LEAST_ARGUMENTS,
          func.name,
          func.maxArguments);
      }
      else if (!isNullOrUndefined(func.maxArguments) && args.length > func.maxArguments) {
        this.addError(syntax,
          CustomFieldIssue.EXPECTED_AT_MOST_ARGUMENTS,
          func.name,
          func.maxArguments);
      }

      if (checkArgumentTypes) {
        let defaultArgCheck = true;

        // IN-927
        if (func.name.toUpperCase() == 'IF') {
          if (args.length > 0) {
            if (args[0].dataType != DataType.Boolean) {
              this.addError(syntax,
                CustomFieldIssue.IN_927_IF_EXPECTS_BOOLEAN,
                func.name);
            }

            if (args.length > 2) {
              let arg1Type = args[1].dataType;
              let arg2Type = args[2].dataType;

              if (!arg1Type.isDirectlyAssignableTo(arg2Type) && !arg2Type.isDirectlyAssignableTo(arg1Type)) {
                this.addError(syntax,
                  CustomFieldIssue.IN_927_INCOMPATIBLE_BRANCH,
                  func.name);
              }
            }
          }

          defaultArgCheck = false;
        }

        if (func.name.toUpperCase() == 'IFBLANK') {
          if (args.length > 0) {
            if (!args[0].dataType.isText || (args.length > 1 && !args[1].dataType.isText)) {
              this.addError(syntax,
                CustomFieldIssue.IN_927_IFBLANK_TEXT,
                func.name);
            }
          }

          defaultArgCheck = false;
        }

        // IN-7226, Scenario 8
        if (['TOLOWER', 'TOUPPER'].includes(func.name.toUpperCase())) {
          if (args.length > 0 && ![DataType.Unknown, DataType.Text].includes(args[0].dataType)) {
            this.addError(syntax, CustomFieldIssue.IN_7226_INVALID_TOUPPER_TOLOWER_ARG, func.name.toUpperCase());
          }

          defaultArgCheck = false;
        }

        if (defaultArgCheck) {
          let invalidArgs = args.some((a, i) => {
            if (func.arguments[i].typeMustMatch) {
              if (a.dataType != DataType.Unknown && a.dataType.value != func.arguments[i].type)
                return false;
            }

            return !a.dataType.isAssignableTo(DataType.from(func.arguments[i].type), false);
          });

          if (invalidArgs) {
            this.addError(syntax, CustomFieldIssue.INVALID_FUNCTION_ARGS, func.name);
          }
        }
      }

      if ((func.returnTypeMode & CustomFieldFunctionReturnTypeMode.ReturnType) != 0) {
        return {
          dataType: DataType.from(func.returnType),
          aggregated: func.aggregatesFields
        };
      }

      if (args.length > 0) {
        if ((func.returnTypeMode & CustomFieldFunctionReturnTypeMode.Args_All) != 0) {
          return {
            dataType: DataType.getLeftSideResult(args.map(x => x.dataType)),
            aggregated: func.aggregatesFields || !!args.find(x => x.aggregated)
          };
        }

        if ((func.returnTypeMode & CustomFieldFunctionReturnTypeMode.Args_First) != 0) {
          return {
            dataType: args[0].dataType,
            aggregated: func.aggregatesFields || args[0].aggregated
          };
        }

        if ((func.returnTypeMode & CustomFieldFunctionReturnTypeMode.Args_IgnoreFirst)) {
          let argsWithoutFirst = args.slice(1);
          return {
            dataType: DataType.getLeftSideResult(argsWithoutFirst.map(x => x.dataType)),
            aggregated: func.aggregatesFields || !!argsWithoutFirst.find(x => x.aggregated)
          };
        }

        if ((func.returnTypeMode & CustomFieldFunctionReturnTypeMode.Args_Last)) {
          return {
            dataType: args[args.length - 1].dataType,
            aggregated: func.aggregatesFields || args[args.length - 1].aggregated
          };
        }
      }

      return {
        dataType: func.returnType ? DataType.from(func.returnType) : DataType.Text,
        aggregated: func.aggregatesFields
      };
    }

    visitIdentifier(syntax: InsightsQuery.IdentifierSyntax, params: ValueValidationParams) {
      let f = this.fieldResolver(syntax, this.options.currentField);
      if (!f) {
        this.addError(syntax, CustomFieldIssue.UNKNOWN_FIELD, syntax.toString());
        return { dataType: DataType.Text };
      }
      else {
        let t = typeof f === 'function' ? f() : f;

        if (!t.isValid) {
          this.addError(syntax, CustomFieldIssue.BROKEN_FIELD, syntax.toString());
        }

        return {
          dataType: t.dataType,
          aggregated: t.isAggregated
        };
      }
    }


    private expectBoolean(syntax: InsightsQuerySyntax, v: DataType) {
      if (!v.isBoolean) {
        this.addError(syntax, CustomFieldIssue.NOT_A_BOOLEAN);
      }
    }

    private expectNumeric(syntax: InsightsQuerySyntax, v: DataType) {
      if (!v.isNumeric) {
        this.addError(syntax, CustomFieldIssue.NOT_A_NUMBER);
      }
    }

    private expectTextPermissive(syntax: InsightsQuerySyntax, v: DataType) {
      if (!v.isAssignableTo(DataType.Text, true)) {
        this.addWarning(syntax, CustomFieldIssue.IMPLICIT_CAST_TO_TEXT);
      }
    }

    private expectText(syntax: InsightsQuerySyntax, v: DataType) {
      if (!v.isText) {
        this.addError(syntax, CustomFieldIssue.NOT_TEXT);
      }
    }

    private expectSameType(
      operationSyntax: InsightsQuerySyntax,
      v1Syntax: InsightsQuerySyntax, v1: DataType,
      v2Syntax: InsightsQuerySyntax, v2: DataType) {
      if (!(v1.isAssignableFrom(v2))) {
        this.addError(
          operationSyntax, CustomFieldIssue.INVALID_CONVERSION,
          v1.singularValueText,
          v2.singularValueText);
      }
    }

    private expectNumbers(
      operationSyntax: InsightsQuerySyntax,
      v1Syntax: InsightsQuerySyntax, v1: DataType,
      v2Syntax: InsightsQuerySyntax, v2: DataType) {
      if (v1.isNumeric && v2.isNumeric)
        return;

      this.addError(
        operationSyntax, CustomFieldIssue.OPERATION_EXPECTS_NUMBERS,
        v1.value,
        this.operationTitle(operationSyntax));

      this.expectNumeric(v1Syntax, v1);
      this.expectNumeric(v2Syntax, v2);
    }


    private addInfo(
      syntaxOrLocation: InsightsQuerySyntax | InsightsQuery.TextLocation,
      descriptor: any,
      ...args: any[]) {
      let location = syntaxOrLocation instanceof InsightsQuerySyntax
        ? syntaxOrLocation.location
        : syntaxOrLocation;

      return this.addIssue(location, CustomFieldIssue.Type.Info, descriptor, ...args);
    }

    private addWarning(
      syntaxOrLocation: InsightsQuerySyntax | InsightsQuery.TextLocation,
      descriptor: any,
      ...args: any[]) {
      let location = syntaxOrLocation instanceof InsightsQuerySyntax
        ? syntaxOrLocation.location
        : syntaxOrLocation;

      return this.addIssue(location, CustomFieldIssue.Type.Warning, descriptor, ...args);
    }

    private addError(
      syntaxOrLocation: InsightsQuerySyntax | InsightsQuery.TextLocation,
      descriptor: any,
      ...args: any[]) {
      let location = syntaxOrLocation instanceof InsightsQuerySyntax
        ? syntaxOrLocation.location
        : syntaxOrLocation;

      return this.addIssue(location, CustomFieldIssue.Type.Error, descriptor, ...args);
    }

    private addIssue(
      location: InsightsQuery.TextLocation,
      type: CustomFieldIssue.Type,
      descriptor: any,
      ...args: any[]) {
      if (descriptor.disabled)
        return;

      this.issues.push({
        type,
        message: descriptor.format(this.escapeArgs(args)),
        location
      });
    }

    escapeArgs(args: any[]) {
      return args.map(arg => {
        if (typeof arg === 'string') {
          return escapeHtml(arg);
        }
        return arg;
      })
    }


    private operationTitle(syntax: InsightsQuerySyntax) {
      if (syntax instanceof InsightsQuery.BinarySyntax) {
        switch (syntax.operator) {
          case InsightsQuery.BinaryOperator.Add: return 'Addition';
          case InsightsQuery.BinaryOperator.Subtract: return 'Subtraction';
          case InsightsQuery.BinaryOperator.Multiply: return 'Multiplication';
          case InsightsQuery.BinaryOperator.Divide: return 'Division';
          case InsightsQuery.BinaryOperator.RaiseTo: return 'Exponent';
          case InsightsQuery.BinaryOperator.And: return 'Logical AND';
          case InsightsQuery.BinaryOperator.Or: return 'Logical OR';
          case InsightsQuery.BinaryOperator.Concat: return 'Concatenation';
          case InsightsQuery.BinaryOperator.Equal: return 'Equality';
          case InsightsQuery.BinaryOperator.NotEqual: return 'Inequality';
        }
      }

      return 'This operation';
    }
  }

  class CirciularDependencyValidationVisitor extends InsightsQuerySyntaxVisitor {
    constructor(
      public readonly options: Options) {
      super();
    }

    field = this.options.field;
    fieldStack: string[] = [this.field];

    visitIdentifier(syntax: InsightsQuery.IdentifierSyntax) {
      let f = this.options.fieldResolver(syntax, this.options.currentField);
      if (!f)
        return null;

      let target = typeof f === 'function' ? f() : f;
      if (target) {
        if (target.identifierName.toLowerCase() === this.field.toLowerCase()) {
          throw new CirciularDependencyError(this.fieldStack);
        }

        let customField = target.customField;
        if (customField) {
          let syntax = customField.syntax();
          if (syntax) {
            this.fieldStack.push(target.identifierName);
            this.visit(syntax);
            this.fieldStack.pop();
          }
        }
      }
    }
  }

  class CirciularDependencyError extends Error {
    constructor(public fieldStack: string[]) {
      super();
    }
  }
}
